import { useMemo, useEffect, useRef } from 'react';
import { useQueries, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { peekAtomicRosterNav, consumeAtomicRosterNav } from './rosterNavIntent';

export interface RosterPillDef<TKey extends string> {
  key: TKey;
  label: string;
  /** BE status query param for this pill; omit for the "all" pill (no status filter). */
  status?: string;
}

export interface RosterPillCount<TKey extends string> {
  key: TKey;
  label: string;
  count: number;
}

/** One server page. Mirrors the BE's paginated envelope as parsed by
 *  `parsePaged` — the shape every roster list hook returns. */
export interface RosterPage<TRow> {
  entries: TRow[];
  total: number;
  totalPages: number;
}

export interface UseRosterStatusTabsOptions<TRow, TKey extends string> {
  campaignId: string | undefined;
  /** Caller-resolved active pill (URL param, deep-link precedence, …). This hook
   *  owns query orchestration and pagination, not URL state. */
  activeTab: TKey;
  pills: RosterPillDef<TKey>[];
  /** Fetches ONE page for the ACTIVE pill only — inactive pills issue no request
   *  at all (their badges come from `counts`). The caller's closure owns the
   *  endpoint + params; while a search is active it may fetch-and-slice the
   *  whole bucket, still returning a single {@link RosterPage}. This is what
   *  replaced the old per-pill whole-bucket (limit:10000) fan-out. */
  buildPageQuery: (
    pill: RosterPillDef<TKey>,
    page: number,
    pageSize: number,
  ) => UseQueryOptions<RosterPage<TRow>>;
  /** Per-pill badge counts, resolved by the caller from `useRosterCounts` so no
   *  pill fetches its rows just to show a number. */
  counts: Partial<Record<TKey, number>>;
  pageSize: number;
  /** Caller-owned page state (URL-synced via useCampaignPageParam). The hook
   *  resets it to 1 on a pill change but never stores its own copy, so
   *  deep-linking (?pill=x&page=n) keeps working unchanged. */
  page: number;
  setPage: (page: number) => void;
  /** Optional client-side row filter applied to the current page for DISPLAY
   *  (activeRows stays unfiltered for logic). Set only in hybrid-search "client
   *  mode", when the whole bucket is already on the page — the hook then reports
   *  `total`/`pageCount` for the FILTERED set so the footer + pager match what's
   *  shown. Undefined = normal server pagination. */
  clientFilter?: (row: TRow) => boolean;
}

export interface UseRosterStatusTabsResult<TRow, TKey extends string> {
  pills: RosterPillCount<TKey>[];
  /** The active pill's current page of rows. */
  pageRows: TRow[];
  /** Alias of `pageRows`. Under server-side pagination there is no whole-bucket
   *  set in memory — this is the current page. Callers that must resolve a row
   *  which may live on another page use the /roster/by-username resolver. */
  activeRows: TRow[];
  total: number;
  pageCount: number;
  isLoading: boolean;
  isError: boolean;
  /** True while the active pill's query is refetching in the background
   *  (SSE-driven invalidation, revalidation) — false during the first load
   *  (that's `isLoading`). Drives "new activity" affordances like JumpToTopPill. */
  isRefetching: boolean;
}

/**
 * Shared fetch/pill/pagination hook for campaign-detail roster tabs
 * (Creators, Confirmed, Scripts, Content, Live-Content).
 *
 * Server-side: ONLY the active pill fetches, and it fetches exactly ONE page.
 * Inactive pills issue no request — their badges come from the single
 * `/roster/counts` aggregation the caller passes as `counts`, so a badge can
 * never race (or disagree with) the row data. `activeTab` and `page` are
 * caller-resolved, so this hook has zero router dependency and every tab keeps
 * its existing URL-synced useCampaignPageParam / useCampaignPillParam untouched.
 */
export function useRosterStatusTabs<TRow, TKey extends string>({
  campaignId,
  activeTab,
  pills,
  buildPageQuery,
  counts,
  pageSize,
  page,
  setPage,
  clientFilter,
}: UseRosterStatusTabsOptions<TRow, TKey>): UseRosterStatusTabsResult<TRow, TKey> {
  // A USER pill change resets pagination to page 1. The refs (seeded with the
  // FIRST render's values) detect the change during render as well as in the
  // effect, so it never fires on mount — a deep link like ?pill=live&page=3
  // must keep page 3.
  //
  // ATOMIC pill+page navigation is exempt from the reset: the deep-link
  // resolver navigates {pill, page} together in one URL update (the BE computed
  // which page the target creator sits on). When BOTH changed in the same
  // render, honoring the given page is the whole point — resetting to 1 would
  // silently lose the resolved page and strand the deep link.
  const prevActiveTabRef = useRef(activeTab);
  const prevPageRef = useRef(page);
  // Reading the refs during render is deliberate change-detection: we need the
  // pill-changed signal in THIS committed render so the query fetches the right
  // page immediately (see effectivePage below). The prev-in-state alternative
  // can't provide it — setState-during-render discards this render, and setPage
  // is caller/URL-owned so it can't be called during render. The refs are still
  // read+written in the effect below to drive the actual reset.
  // Atomic detection is TWO signals OR-ed: (a) page changed in the same render
  // as the pill (value diff), and (b) the deep-link resolver's explicit intent
  // flag (rosterNavIntent) — needed because the resolved page can NUMERICALLY
  // EQUAL the page the user was already on, which value-diffing alone misreads
  // as a plain pill click and wrongly resets to 1.
  // eslint-disable-next-line react-hooks/refs -- intentional prev-value read; see note above
  const pillChanged = prevActiveTabRef.current !== activeTab;
  // eslint-disable-next-line react-hooks/refs -- intentional prev-value read; see note above
  const pageChangedWithPill = pillChanged && prevPageRef.current !== page;
  const pillJustChanged = pillChanged && !pageChangedWithPill && !peekAtomicRosterNav();
  useEffect(() => {
    if (prevActiveTabRef.current === activeTab) {
      prevPageRef.current = page; // plain page turn — just track it
      // Discard any stale intent flag: this transition consumed nothing
      // pill-related, and a lingering flag would wrongly suppress the reset
      // on the NEXT genuine pill click.
      consumeAtomicRosterNav();
      return;
    }
    const atomicPillPageNav = prevPageRef.current !== page || consumeAtomicRosterNav();
    prevActiveTabRef.current = activeTab;
    prevPageRef.current = page;
    if (!atomicPillPageNav) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page]);

  // The setPage(1) above lands one render late (it's caller-owned state), so on
  // the render where the pill changed `page` still holds the previous value.
  // Query the NEW pill at page 1 immediately — otherwise we'd fire a throwaway
  // fetch for the old page number, then refetch page 1 once the reset commits.
  const effectivePage = pillJustChanged ? 1 : page;

  const activePill = useMemo(
    () => pills.find((p) => p.key === activeTab) ?? pills[0],
    [pills, activeTab],
  );

  // A single query — the active pill's page. Kept inside a useQueries array
  // (length 1, or 0 before pills resolve) so hook order is stable regardless of
  // how many pills the tab has.
  //
  // `built.enabled` (from the caller's `buildPageQuery` closure) is ANDed
  // with `!!campaignId`, not overwritten by it — a caller that needs to
  // suppress this query for a reason OTHER than a missing campaignId (e.g.
  // the public read-only share view, which has a real campaignId available
  // in some deployments but must never fetch it) has a real, honored lever.
  // Previously `enabled: !!campaignId` was spread AFTER `...buildPageQuery(...)`,
  // silently clobbering whatever `enabled` the closure returned.
  const queryConfigs = useMemo<UseQueryOptions<RosterPage<TRow>>[]>(() => {
    if (!activePill) return [];
    const built = buildPageQuery(activePill, effectivePage, pageSize);
    return [
      {
        placeholderData: keepPreviousData,
        ...built,
        enabled: !!campaignId && (built.enabled ?? true),
      },
    ];
  }, [campaignId, activePill, buildPageQuery, effectivePage, pageSize]);

  const queries = useQueries({ queries: queryConfigs });
  const activeQuery = queries[0];

  // NOT `activeQuery?.isLoading` — TanStack Query v5 reports isLoading=false for
  // a *disabled* query (enabled gates on campaignId), which reads as "settled"
  // when it's really "no answer yet". A pill is settled once it has data OR an
  // error — so a cascade gated on this flag can't fire on the brief pre-campaignId
  // window with stale zero data.
  const isLoading = !!activeQuery && activeQuery.data === undefined && !activeQuery.isError;
  const isError = !!activeQuery?.isError;
  const isRefetching = activeQuery?.isRefetching ?? false;

  const serverPage = activeQuery?.data;
  const entries = serverPage?.entries ?? [];
  // Client-mode search (hybrid): the whole bucket is already on the page, so the
  // caller passes a predicate to filter it locally for display. activeRows stays
  // unfiltered for logic (approve-all, dialog lookup); total/pageCount report the
  // filtered set so the footer + pager match what's shown.
  const displayRows = clientFilter ? entries.filter(clientFilter) : entries;
  const pageCount = clientFilter ? 1 : Math.max(1, serverPage?.totalPages ?? 1);

  // Prefetch-on-intent (idle): warm the NEXT page once the current one settles so
  // paging forward is instant. Guardrails — only when a next page exists, skipped
  // in client-filter mode (the whole set is already on the page), one prefetch per
  // settle, cancelled on change; same 30s staleTime means a re-trigger over an
  // already-warm page is a free no-op.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!activePill || !serverPage || clientFilter) return;
    const nextPage = effectivePage + 1;
    if (nextPage > pageCount) return;
    const nextOpts = buildPageQuery(activePill, nextPage, pageSize);
    // Whole-bucket (select-sliced) branches key by (pill, search) only — the
    // "next page" is the SAME cache entry, already resident, so paging forward
    // is instant with nothing to warm. Skip rather than re-touch the cache.
    if (JSON.stringify(nextOpts.queryKey) === JSON.stringify(queryConfigs[0]?.queryKey)) return;
    const run = () =>
      queryClient.prefetchQuery({
        ...nextOpts,
        staleTime: 30_000,
      });
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const id = w.requestIdleCallback
      ? w.requestIdleCallback(run)
      : (setTimeout(run, 250) as unknown as number);
    return () => {
      if (w.requestIdleCallback && w.cancelIdleCallback) w.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [activePill, serverPage, effectivePage, pageSize, pageCount, clientFilter, buildPageQuery, queryClient, queryConfigs]);

  // Badges come from the shared /roster/counts aggregation, so an inactive pill
  // shows a correct total without ever fetching its rows.
  const pillCounts: RosterPillCount<TKey>[] = pills.map((pill) => ({
    key: pill.key,
    label: pill.label,
    count: counts?.[pill.key] ?? 0,
  }));

  return {
    pills: pillCounts,
    pageRows: displayRows,
    activeRows: entries,
    total: clientFilter ? displayRows.length : serverPage?.total ?? 0,
    pageCount,
    isLoading,
    isError,
    isRefetching,
  };
}
